import * as _ from 'lodash';
import { AttributeUiType } from 'src/app/shared/models/attributes/attributeUiType';
import { AttributeEntityType } from './attributeEntityType';

export class AttributeUiTypeDetails {
  constructor(public uiType: AttributeUiType) {}

  get icon(): string {
    switch (this.uiType) {
      case AttributeUiType.ShortAnswer:
        return 'short_text';
      case AttributeUiType.LongAnswer:
        return 'subject';
      case AttributeUiType.Checkboxes:
        return 'check_box';
      case AttributeUiType.DropDown:
        return 'arrow_drop_down_circle';
      case AttributeUiType.Number:
        return 'looks_one';
      case AttributeUiType.Date:
        return 'event';
      case AttributeUiType.MultiSelect:
        return 'checklist';
      case AttributeUiType.Skill:
        return 'psychology';
      case AttributeUiType.StarRating:
        return 'star';
      case AttributeUiType.Document:
        return 'upload_file';
      case AttributeUiType.DateRange:
        return 'date_range';
      case AttributeUiType.Complex:
        return 'category';
      case AttributeUiType.EncryptedField:
        return 'password';
      default:
        return 'contact_support';
    }
  }

  get name(): string {
    switch (this.uiType) {
      case AttributeUiType.ShortAnswer:
        return 'Short Answer';
      case AttributeUiType.LongAnswer:
        return 'Long Answer';
      case AttributeUiType.Checkboxes:
        return 'Checkbox';
      case AttributeUiType.DropDown:
        return 'Dropdown';
      case AttributeUiType.Number:
        return 'Number';
      case AttributeUiType.Date:
        return 'Date';
      case AttributeUiType.MultiSelect:
        return 'Multi-select';
      case AttributeUiType.Skill:
        return 'Skill';
      case AttributeUiType.StarRating:
        return 'Star Rating';
      case AttributeUiType.Document:
        return 'Document';
      case AttributeUiType.DateRange:
        return 'Date Range';
      case AttributeUiType.Complex:
        return 'Complex Field';
      case AttributeUiType.EncryptedField:
        return 'Encrypted Field';
      default:
        return 'Unknown';
    }
  }

  get hasNumberRange(): boolean {
    return this.uiType == AttributeUiType.Number;
  }

  // Does the UI currently support this AttributeUiType?
  get isSupported(): boolean {
    switch (this.uiType) {
      case AttributeUiType.ShortAnswer:
      case AttributeUiType.LongAnswer:
      case AttributeUiType.DropDown:
      case AttributeUiType.Checkboxes:
      case AttributeUiType.MultiSelect:
      case AttributeUiType.Number:
      case AttributeUiType.StarRating:
      case AttributeUiType.Date:
      case AttributeUiType.DateRange:
        return true;
      default:
        return false;
    }
  }

  get isAdvanced(): boolean {
    switch (this.uiType) {
      case AttributeUiType.StarRating:
      case AttributeUiType.Date:
      case AttributeUiType.Document:
      case AttributeUiType.Complex:
        return true;
      default:
        return false;
    }
  }

  get isOnlyForStaff(): boolean {
    // add a switch operation on this.uiType to return true for those types we want for staff only
    switch (this.uiType) {
      case AttributeUiType.EncryptedField:
        return true;
      default:
        return false;
    }
  }

  get isOnlySupportedForPerson(): boolean {
    switch (this.uiType) {
      case AttributeUiType.Document:
      case AttributeUiType.EncryptedField:
      case AttributeUiType.Complex:
        return true;
      default:
        return false;
    }
  }

  get isOnlySupportedForTeam(): boolean {
    switch (this.uiType) {
      case AttributeUiType.Document:
      case AttributeUiType.EncryptedField:
        return true;
      default:
        return false;
    }
  }

  get isEnumerated(): boolean {
    return _.find(AttributeUiTypeDetails.enumeratedUiTypes, (x) => x === this.uiType) !== undefined;
  }

  get isSupportedForSubFieldItems(): boolean {
    switch (this.uiType) {
      case AttributeUiType.Document:
      case AttributeUiType.Complex:
      case AttributeUiType.Skill:
        return false;
      default:
        return true;
    }
  }

  canBeFeatured(entityType: AttributeEntityType): boolean {
    return this.isEnumerated && entityType === AttributeEntityType.Person;
  }

  canBeShownOnCreate(entityType: AttributeEntityType): boolean {
    return entityType === AttributeEntityType.Team && this.uiType !== AttributeUiType.Document;
  }

  canBeVisualized(entityType: AttributeEntityType): boolean {
    // Happens to share the same logic as canBeFeatured, but not guaranteed to always be true.
    return this.canBeFeatured(entityType);
  }

  canBeFiltered(): boolean {
    switch (this.uiType) {
      case AttributeUiType.Checkboxes:
      case AttributeUiType.DropDown:
      case AttributeUiType.ShortAnswer:
      case AttributeUiType.LongAnswer:
      case AttributeUiType.Number:
      case AttributeUiType.Date:
      case AttributeUiType.Toggle:
      case AttributeUiType.RadioButtonGroup:
      case AttributeUiType.Document:
      case AttributeUiType.MultiSelect:
      case AttributeUiType.StarRating:
      case AttributeUiType.Skill:
      case AttributeUiType.DateRange:
      case AttributeUiType.Complex:
      case AttributeUiType.EncryptedField:
        return true;
      default:
        return false;
    }
  }

  // Does this AttributeUiType + AttiributeEntityType combination
  // support any togglable settings?
  isToggleable(entityType: AttributeEntityType): boolean {
    return (
      this.canBeFeatured(entityType) ||
      this.canBeVisualized(entityType) ||
      this.canBeFiltered() ||
      this.canBeShownOnCreate(entityType)
    );
  }

  static get all(): AttributeUiTypeDetails[] {
    // TODO: There is probably a cleaner way to do this by iterating over
    // AttributeUiType keys.
    return [
      new AttributeUiTypeDetails(AttributeUiType.ShortAnswer),
      new AttributeUiTypeDetails(AttributeUiType.LongAnswer),
      new AttributeUiTypeDetails(AttributeUiType.Number),
      new AttributeUiTypeDetails(AttributeUiType.DropDown),
      new AttributeUiTypeDetails(AttributeUiType.Checkboxes),
      new AttributeUiTypeDetails(AttributeUiType.MultiSelect),
      new AttributeUiTypeDetails(AttributeUiType.Date),
      new AttributeUiTypeDetails(AttributeUiType.DateRange),
      new AttributeUiTypeDetails(AttributeUiType.RadioButtonGroup),
      new AttributeUiTypeDetails(AttributeUiType.Toggle),
      new AttributeUiTypeDetails(AttributeUiType.Skill),
      new AttributeUiTypeDetails(AttributeUiType.StarRating),
      new AttributeUiTypeDetails(AttributeUiType.Document),
      new AttributeUiTypeDetails(AttributeUiType.Complex),
      new AttributeUiTypeDetails(AttributeUiType.EncryptedField),
    ];
  }

  static supported(
    entityType: AttributeEntityType = AttributeEntityType.Person
  ): AttributeUiTypeDetails[] {
    if (entityType && entityType == AttributeEntityType.Person) {
      return _.filter(
        AttributeUiTypeDetails.all,
        (item) => item.isSupported || item.isOnlySupportedForPerson
      );
    } else if (entityType && entityType == AttributeEntityType.Team) {
      return _.filter(
        AttributeUiTypeDetails.all,
        (item) => item.isSupported || item.isOnlySupportedForTeam
      );
    } else {
      return _.filter(AttributeUiTypeDetails.all, (item) => item.isSupported);
    }
  }

  static supportedForSubField(
    entityType: AttributeEntityType = AttributeEntityType.Person
  ): AttributeUiTypeDetails[] {
    return _.filter(this.supported(entityType), (item) => item.isSupportedForSubFieldItems);
  }

  static get enumeratedUiTypes(): AttributeUiType[] {
    return [
      AttributeUiType.Checkboxes,
      AttributeUiType.DropDown,
      AttributeUiType.MultiSelect,
      AttributeUiType.Skill,
    ];
  }
}
